import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback, useState } from "react";
import { ContractNature, NeedPriority } from "../../../autogen/bff-api";

export type NeedTypeSuggestion = {
  procurementType: ContractNature;
  priority: NeedPriority;
  reasoning: string;
};

type Props = {
  model: AgentTaskModel;
  fetchClientToken: () => Promise<{ clientToken: string }>;
};

const SUGGESTION_SCHEMA = JSON.stringify({
  type: "object",
  additionalProperties: false,
  properties: {
    procurementType: {
      type: "string",
      enum: ["SUPPLIES", "SERVICES", "WORKS"],
      description: "SUPPLIES for goods/products, SERVICES for services/advisory, WORKS for building and construction.",
    },
    priority: {
      type: "string",
      enum: ["HIGH", "MEDIUM", "LOW"],
      description: "Estimated urgency of the need.",
    },
    reasoning: { type: "string", description: "A short justification in Norwegian, max ~25 words." },
  },
  required: ["procurementType", "priority", "reasoning"],
});

export const useNeedTypeSuggestion = ({ model, fetchClientToken }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const fetchToken = useCallback(
    async (taskType: "StreamingChatCompletion" | "StructuredChatCompletion" | "MemoryChat" | "MemorySearch") => {
      void taskType;
      return fetchClientToken();
    },
    [fetchClientToken],
  );

  const { createAgentTask } = useAgentTasks({ fetchCreateAgentTaskClientToken: fetchToken });

  const suggest = useCallback(
    async (title: string, description: string): Promise<NeedTypeSuggestion | null> => {
      setIsLoading(true);
      setHasError(false);
      try {
        const result = await createAgentTask({
          type: "StructuredChatCompletion",
          model,
          jsonSchema: SUGGESTION_SCHEMA,
          messages: buildMessages(title, description),
        });
        if (result.type !== "success" || result.response.taskType !== "StructuredChatCompletion") {
          setHasError(true);
          return null;
        }
        return parseSuggestion(result.response.response.jsonResponse);
      } catch {
        setHasError(true);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [createAgentTask, model],
  );

  return { suggest, isLoading, hasError };
};

const buildMessages = (title: string, description: string) => [
  {
    role: "SYSTEM" as const,
    content: [
      "You are an assistant for Norwegian public procurement.",
      "Given a procurement need's title and description, classify the contract type and estimate the priority.",
      "Write the reasoning in Norwegian.",
    ].join(" "),
  },
  {
    role: "USER" as const,
    content: [`Title: ${title || "(no title)"}`, `Description:\n${description || "(no description)"}`].join("\n\n"),
  },
];

const TYPES: Record<string, ContractNature> = {
  SUPPLIES: ContractNature.Supplies,
  SERVICES: ContractNature.Services,
  WORKS: ContractNature.Works,
};

const PRIORITIES: Record<string, NeedPriority> = {
  HIGH: NeedPriority.High,
  MEDIUM: NeedPriority.Medium,
  LOW: NeedPriority.Low,
};

const parseSuggestion = (json: string): NeedTypeSuggestion | null => {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  const obj = data as { procurementType?: unknown; priority?: unknown; reasoning?: unknown };
  const procurementType = TYPES[String(obj.procurementType ?? "")];
  const priority = PRIORITIES[String(obj.priority ?? "")];
  if (!procurementType || !priority) return null;
  return { procurementType, priority, reasoning: String(obj.reasoning ?? "").trim() };
};
